import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, ChevronRight, ChevronLeft,
  Shield, Clock, Star,
  Leaf, Wheat, Milk, Flame, Fish, Heart, Nut, ArrowRight,
  Cookie, Coffee
} from 'lucide-react';
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase';
import ProductCard from '../Products/ProductCard';
import ProductDetailView from '../Products/ProductDetailView';
import RecipeDetailView from '../Recipes/RecipeDetailView';

const DIETARY_FILTERS = [
  { value: 'all',         label: 'All',          icon: null   },
  { value: 'glutenFree',  label: 'Gluten-Free',  icon: Wheat  },
  { value: 'dairyFree',   label: 'Dairy-Free',   icon: Milk   },
  { value: 'nutFree',     label: 'Nut-Free',     icon: Nut    },
  { value: 'vegan',       label: 'Vegan',        icon: Leaf   },
  { value: 'keto',        label: 'Keto',         icon: Flame  },
  { value: 'pescatarian', label: 'Pescatarian',  icon: Fish   },
  { value: 'halal',       label: 'Halal',        icon: Heart  },
];

const CATEGORY_TILES = [
  { label: 'Snacks',      icon: Cookie, diet: 'all'        },
  { label: 'Beverages',   icon: Coffee, diet: 'all'        },
  { label: 'Dairy-Free',  icon: Milk,   diet: 'dairyFree'  },
  { label: 'Vegan',       icon: Leaf,   diet: 'vegan'      },
  { label: 'Keto',        icon: Flame,  diet: 'keto'       },
  { label: 'Gluten-Free', icon: Wheat,  diet: 'glutenFree' },
];

const HomeView = () => {
  const navigate = useNavigate();

  const [trendingRecipes,   setTrendingRecipes]   = useState([]);
  const [trendingProducts,  setTrendingProducts]  = useState([]);
  const [loading,           setLoading]           = useState(true);
  const [selectedProduct,   setSelectedProduct]   = useState(null);
  const [selectedRecipe,    setSelectedRecipe]    = useState(null);
  const [searchQuery,       setSearchQuery]       = useState('');
  const [activeFilter,      setActiveFilter]      = useState('all');

  const recipesScrollRef  = useRef(null);
  const productsScrollRef = useRef(null);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        setLoading(true);

        // Products
        try {
          const snap = await getDocs(
            query(collection(db, 'products'), orderBy('createdAt', 'desc'), limit(12))
          );
          setTrendingProducts(
            snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(p => p.isVerified === true)
          );
        } catch (e) { console.error('Products error:', e); }

        // Recipes
        try {
          const snap = await getDocs(
            query(collection(db, 'recipes'), orderBy('createdAt', 'desc'), limit(10))
          );
          setTrendingRecipes(
            snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(r => r.isPublic === true)
          );
        } catch (e) { console.error('Recipes error:', e); }

      } catch (err) {
        console.error('Fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, []);

  const scroll = (ref, dir) => {
    if (ref.current) ref.current.scrollBy({ left: dir === 'left' ? -320 : 320, behavior: 'smooth' });
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) navigate(`/products?q=${encodeURIComponent(searchQuery.trim())}`);
  };

  const filteredProducts = activeFilter === 'all'
    ? trendingProducts
    : trendingProducts.filter(p =>
        p.dietaryTags?.includes(activeFilter) || p[activeFilter] === true
      );

  if (loading) {
    return (
      <div className="text-center py-xxxl">
        <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-primary mx-auto mb-md" />
        <p className="text-callout text-text-secondary">Loading...</p>
      </div>
    );
  }

  if (selectedProduct) {
    return <ProductDetailView product={selectedProduct} onBack={() => setSelectedProduct(null)} />;
  }

  if (selectedRecipe) {
    return <RecipeDetailView recipe={selectedRecipe} onBack={() => setSelectedRecipe(null)} />;
  }

  return (
    <div className="space-y-xxxl pb-xxxl">

      {/* ── Search bar ── */}
      <section>
        <form onSubmit={handleSearch} className="relative max-w-2xl mx-auto">
          <Search
            size={20}
            className="absolute left-lg top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none"
          />
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search products, brands, ingredients…"
            className="w-full pl-14 pr-xl py-lg rounded-2xl bg-surface border border-text-tertiary/20 text-body text-text-primary placeholder:text-text-tertiary focus:outline-none focus:border-primary/40 focus:ring-2 focus:ring-primary/10 transition-all shadow-sm"
          />
          {searchQuery && (
            <button
              type="submit"
              className="absolute right-md top-1/2 -translate-y-1/2 px-md py-xs bg-primary text-white text-caption font-semibold rounded-xl hover:bg-primary/90 transition-all"
            >
              Search
            </button>
          )}
        </form>
      </section>

      {/* ── Category tiles ── */}
      <section>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-md">
          {CATEGORY_TILES.map(({ label, icon: Icon, diet }) => (
            <button
              key={label}
              onClick={() => navigate(`/products?diet=${diet}`)}
              className="flex flex-col items-center gap-sm p-lg bg-surface rounded-2xl hover:bg-primary/5 hover:border-primary/20 border border-transparent transition-all group"
            >
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-all">
                <Icon size={18} className="text-primary" />
              </div>
              <span className="text-caption font-semibold text-text-primary text-center leading-tight">
                {label}
              </span>
            </button>
          ))}
        </div>
      </section>

      {/* ── Products ── */}
      <section>
        <div className="flex items-center justify-between mb-lg">
          <h2 className="text-title-1 font-rounded text-text-primary">Verified Products</h2>
          <div className="flex items-center gap-sm">
            <button
              onClick={() => scroll(productsScrollRef, 'left')}
              className="w-8 h-8 rounded-full bg-surface border border-text-tertiary/15 flex items-center justify-center hover:bg-primary/10 transition-all"
            >
              <ChevronLeft size={16} className="text-primary" />
            </button>
            <button
              onClick={() => scroll(productsScrollRef, 'right')}
              className="w-8 h-8 rounded-full bg-surface border border-text-tertiary/15 flex items-center justify-center hover:bg-primary/10 transition-all"
            >
              <ChevronRight size={16} className="text-primary" />
            </button>
            <button
              onClick={() => navigate('/products')}
              className="text-callout font-semibold text-primary hover:underline flex items-center gap-xs"
            >
              See all <ChevronRight size={14} />
            </button>
          </div>
        </div>

        {/* Dietary filter pills */}
        <div className="flex gap-sm overflow-x-auto pb-sm mb-lg" style={{ scrollbarWidth: 'none' }}>
          {DIETARY_FILTERS.map(({ value, label, icon: Icon }) => (
            <button
              key={value}
              onClick={() => setActiveFilter(value)}
              className={`flex items-center gap-xs px-md py-xs rounded-full text-caption font-semibold whitespace-nowrap border transition-all flex-shrink-0 ${
                activeFilter === value
                  ? 'bg-primary text-white border-primary'
                  : 'bg-surface text-text-secondary border-text-tertiary/20 hover:border-primary/30'
              }`}
            >
              {Icon && <Icon size={12} />}
              {label}
            </button>
          ))}
        </div>

        {filteredProducts.length === 0 ? (
          <div className="text-center py-xl text-text-secondary text-callout">
            No products yet — check back soon.
          </div>
        ) : (
          <div
            ref={productsScrollRef}
            className="flex gap-md overflow-x-auto pb-md"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {filteredProducts.map(product => (
              <div
                key={product.id}
                className="flex-shrink-0 w-[200px] h-[320px] flex flex-col overflow-hidden"
              >
                <ProductCard
                  product={product}
                  onClick={() => setSelectedProduct(product)}
                  viewMode="grid"
                />
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ── Recipes ── */}
      <section>
        <div className="flex items-center justify-between mb-lg">
          <h2 className="text-title-1 font-rounded text-text-primary">Recipes</h2>
          <div className="flex items-center gap-sm">
            <button
              onClick={() => scroll(recipesScrollRef, 'left')}
              className="w-8 h-8 rounded-full bg-surface border border-text-tertiary/15 flex items-center justify-center hover:bg-primary/10 transition-all"
            >
              <ChevronLeft size={16} className="text-primary" />
            </button>
            <button
              onClick={() => scroll(recipesScrollRef, 'right')}
              className="w-8 h-8 rounded-full bg-surface border border-text-tertiary/15 flex items-center justify-center hover:bg-primary/10 transition-all"
            >
              <ChevronRight size={16} className="text-primary" />
            </button>
            <button
              onClick={() => navigate('/recipes')}
              className="text-callout font-semibold text-primary hover:underline flex items-center gap-xs"
            >
              See all <ChevronRight size={14} />
            </button>
          </div>
        </div>

        {trendingRecipes.length === 0 ? (
          <div className="text-center py-xl text-text-secondary text-callout">
            Recipes coming soon.
          </div>
        ) : (
          <div
            ref={recipesScrollRef}
            className="flex gap-lg overflow-x-auto pb-md"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {trendingRecipes.map(recipe => (
              <div
                key={recipe.id}
                onClick={() => setSelectedRecipe(recipe)}
                className="flex-shrink-0 w-[260px] bg-surface rounded-2xl overflow-hidden cursor-pointer hover:shadow-medium transition-all group border border-text-tertiary/08"
              >
                {(recipe.imageURL || recipe.imageUrl) && (
                  <div className="h-40 overflow-hidden">
                    <img
                      src={recipe.imageURL || recipe.imageUrl}
                      alt={recipe.title || recipe.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                )}
                <div className="p-md">
                  {recipe.mealType && (
                    <p className="text-xs font-semibold text-text-tertiary uppercase tracking-wide mb-xs">
                      {recipe.mealType}
                    </p>
                  )}
                  <h3 className="text-callout font-semibold text-text-primary mb-xs line-clamp-1">
                    {recipe.title || recipe.name}
                  </h3>
                  <p className="text-caption text-text-secondary mb-sm line-clamp-2">
                    {recipe.description}
                  </p>
                  <div className="flex items-center gap-md text-caption text-text-tertiary">
                    {(recipe.totalTimeMinutes || recipe.prepTime) > 0 && (
                      <span className="flex items-center gap-xs">
                        <Clock size={12} />
                        {recipe.totalTimeMinutes || recipe.prepTime}m
                      </span>
                    )}
                    {recipe.rating > 0 && (
                      <span className="flex items-center gap-xs">
                        <Star size={12} className="fill-accent text-accent" />
                        {Number(recipe.rating).toFixed(1)}
                      </span>
                    )}
                  </div>
                  {recipe.dietaryTags?.length > 0 && (
                    <div className="flex gap-xs mt-sm flex-wrap">
                      {recipe.dietaryTags.slice(0, 2).map(tag => (
                        <span
                          key={tag}
                          className="px-xs py-0.5 bg-primary/10 text-primary text-xs rounded-small font-medium capitalize"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ── Verified badge strip ── */}
      <section className="flex items-center gap-md p-lg rounded-2xl bg-primary/4 border border-primary/10">
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
          <Shield size={18} className="text-primary" />
        </div>
        <div className="flex-1">
          <p className="text-callout font-semibold text-text-primary">Human Verified</p>
          <p className="text-caption text-text-secondary">
            Every product is reviewed before receiving a verified badge. Allergens, ingredients, dietary tags — all confirmed by a real person.
          </p>
        </div>
        <button
          onClick={() => navigate('/products')}
          className="text-caption font-semibold text-primary flex items-center gap-xs flex-shrink-0 hover:underline"
        >
          Browse <ArrowRight size={14} />
        </button>
      </section>

      <style>{`div::-webkit-scrollbar { display: none; }`}</style>
    </div>
  );
};

export default HomeView;